import { COMPANY, NAV_LINKS } from "../../lib/constants";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";

const SERVICE_AREAS = [
  "Winnipeg HQ",
  "Manitoba",
  "Ontario",
  "Saskatchewan",
  "Alberta",
  "BC",
  "Quebec",
  "Atlantic Canada",
  "Remote / Northern",
];

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black border-t border-white/10 overflow-hidden"
    >
      <div className="absolute inset-0 tb-grid-dense opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-12">
        <h3 className="font-display font-black tracking-tighter text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.85]">
          LET'S MAP <br />
          <span className="text-[#3DC0AF]">THE FUTURE.</span>
        </h3>

        <div className="mt-16 grid md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <div className="flex items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/3gk1w13g_FullLogo_Transparent_NoBuffer%20%2812%29.png"
                alt="Thunderbird Geospatial Inc."
                className="h-12 w-auto object-contain"
                draggable="false"
              />
            </div>
            <div
              data-testid="footer-ownership-badge"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#3DC0AF]/40 bg-[#3DC0AF]/10 px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase font-mono text-[#3DC0AF]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#3DC0AF] animate-pulse" />
              Locally &amp; Indigenous-Owned
            </div>
            <p className="mt-4 text-zinc-400 text-sm leading-relaxed max-w-sm">
              Aerial surveying, drone mapping, and geospatial intelligence
              purpose-built for Canada's builders, planners, and landowners.
            </p>

            <div className="mt-8 space-y-3 text-sm">
              <a
                href={COMPANY.phoneHref}
                data-testid="footer-phone"
                className="flex items-center gap-3 text-zinc-300 hover:text-white"
              >
                <Phone className="h-4 w-4 text-[#5EE3CE]" />
                {COMPANY.phone}
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="footer-email"
                className="flex items-center gap-3 text-zinc-300 hover:text-white"
              >
                <Mail className="h-4 w-4 text-[#5EE3CE]" />
                {COMPANY.email}
              </a>
              <div className="flex items-start gap-3 text-zinc-400">
                <MapPin className="mt-0.5 h-4 w-4 text-[#5EE3CE]" />
                <span>{COMPANY.address}</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] tracking-[0.3em] uppercase font-mono text-zinc-500 mb-5">
              / Navigate
            </div>
            <ul className="space-y-3 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                    className="text-zinc-300 hover:text-white inline-flex items-center gap-1"
                  >
                    {l.label}
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/verify"
                  data-testid="footer-nav-verify"
                  className="text-zinc-400 hover:text-white inline-flex items-center gap-1 text-[13px]"
                >
                  Verify a certificate
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.3em] uppercase font-mono text-zinc-500 mb-5">
              / Service areas
            </div>
            <div className="flex flex-wrap gap-2">
              {SERVICE_AREAS.map((a) => (
                <span
                  key={a}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-zinc-300"
                >
                  {a}
                </span>
              ))}
            </div>

            <a
              href="/#quote"
              data-testid="footer-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#3DC0AF] hover:bg-[#00BA8B] text-white text-sm font-medium px-5 py-3 transition-colors"
            >
              Start a project <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div
          data-testid="land-acknowledgement"
          className="mt-16 pt-8 border-t border-white/10"
        >
          <div className="flex items-start gap-4 max-w-3xl">
            <span className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#3DC0AF]/40 bg-[#3DC0AF]/10 text-[10px] font-mono tracking-[0.2em] uppercase text-[#3DC0AF]">
              ◆
            </span>
            <div>
              <div className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#3DC0AF]">
                Land Acknowledgement
              </div>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                Thunderbird Geospatial Inc. acknowledges that we operate on
                Treaty One Territory, the traditional lands of the Anishinaabeg,
                Ininiwak (Cree), Anisininewuk (Oji-Cree), Dakota Oyate, and Dene
                peoples, and the homeland of the Red River Métis Nation.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-500 font-mono tracking-wide">
          <div>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <span>WINNIPEG · MB · CANADA</span>
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Accepting projects
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
